/**
 * JobSelectionStep Component
 *
 * Phase 8.1: Step 2 of the v2 assessment wizard
 * Allows user to select which Job (SOP section) to assess
 */

import { CheckCircle2, Briefcase, FileQuestion, AlertCircle } from 'lucide-react';

interface Section {
  id: string;
  sectionNumber: number;
  title: string;
  description?: string | null;
  taskCount: number;
  questionCount?: number; // Optional for backward compatibility
}

interface JobSelectionStepProps {
  sections: Section[];
  selectedSectionId: string | null;
  onSelect: (sectionId: string) => void;
  sopTitle?: string;
}

export function JobSelectionStep({
  sections,
  selectedSectionId,
  onSelect,
  sopTitle,
}: JobSelectionStepProps) {
  // Filter sections that have questions (assessment-ready)
  const assessableSections = sections.filter((s) => (s.questionCount ?? 0) > 0);
  const nonAssessableSections = sections.filter((s) => (s.questionCount ?? 0) === 0);

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Select Job to Assess</h2>
        <p className="text-gray-500">
          {sopTitle && (
            <span className="font-medium text-gray-700">{sopTitle}</span>
          )}
          {sopTitle && ' — '}
          Choose which job (section) you want to assess the operator on.
        </p>
      </div>

      {/* Assessable Sections */}
      {assessableSections.length > 0 ? (
        <div className="grid gap-4">
          {assessableSections.map((section) => {
            const isSelected = selectedSectionId === section.id;

            return (
              <button
                key={section.id}
                onClick={() => onSelect(section.id)}
                className={`
                  w-full text-left p-5 rounded-xl border-2 transition-all duration-200
                  ${
                    isSelected
                      ? 'border-[#00674a] bg-[#00674a]/5 shadow-lg shadow-[#00674a]/10'
                      : 'border-gray-200 bg-white hover:border-[#00674a]/30 hover:shadow-md'
                  }
                `}
              >
                <div className="flex items-start gap-4">
                  {/* Selection indicator */}
                  <div
                    className={`
                      w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0
                      ${isSelected ? 'bg-[#00674a] text-white' : 'bg-gray-100 text-gray-400'}
                    `}
                  >
                    {isSelected ? (
                      <CheckCircle2 className="w-5 h-5" />
                    ) : (
                      <Briefcase className="w-5 h-5" />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-[#00674a] bg-[#c5e8c2] px-2 py-0.5 rounded">
                        Job {section.sectionNumber}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {section.title}
                    </h3>
                    {section.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-3">
                        {section.description}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-sm">
                      <div className="flex items-center gap-1.5 text-gray-500">
                        <Briefcase className="w-4 h-4" />
                        <span>{section.taskCount} tasks</span>
                      </div>
                      {(section.questionCount ?? 0) > 0 && (
                        <div className="flex items-center gap-1.5 text-[#00674a] font-medium">
                          <FileQuestion className="w-4 h-4" />
                          <span>{section.questionCount} questions</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Radio indicator */}
                  <div
                    className={`
                      w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0
                      ${isSelected ? 'border-[#00674a] bg-[#00674a]' : 'border-gray-300'}
                    `}
                  >
                    {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-white" />}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 bg-gray-50 rounded-xl border border-gray-200">
          <AlertCircle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No assessable jobs found
          </h3>
          <p className="text-gray-500 max-w-md mx-auto">
            This SOP doesn't have any jobs with assessment questions yet.
            Please process the SOP first to generate questions.
          </p>
        </div>
      )}

      {/* Non-assessable sections (collapsed) */}
      {nonAssessableSections.length > 0 && (
        <div className="mt-8 pt-6 border-t border-gray-200">
          <p className="text-sm text-gray-500 mb-3">
            {nonAssessableSections.length} job{nonAssessableSections.length > 1 ? 's' : ''} without
            assessment questions:
          </p>
          <div className="flex flex-wrap gap-2">
            {nonAssessableSections.map((section) => (
              <span
                key={section.id}
                className="px-3 py-1.5 bg-gray-100 text-gray-500 text-sm rounded-lg"
              >
                {section.title}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default JobSelectionStep;
